import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  Link2,
  Loader2,
  Package,
  ShoppingBag,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const MARKETPLACES = [
  { id: 'amazon', label: 'Amazon', href: 'https://www.amazon.com' },
  { id: 'walmart', label: 'Walmart', href: 'https://www.walmart.com' },
  { id: 'ebay', label: 'eBay', href: 'https://www.ebay.com' },
  { id: 'temu', label: 'Temu', href: 'https://www.temu.com' },
  { id: 'aliexpress', label: 'AliExpress', href: 'https://www.aliexpress.com' },
  { id: 'alibaba', label: 'Alibaba', href: 'https://www.alibaba.com' },
];

const STEPS = [
  { title: 'Paste product link', detail: 'From any supported marketplace' },
  { title: 'ONE final price', detail: '20% margin applied on the server' },
  { title: 'Pay & we import', detail: 'Added straight to your cart' },
];

function safeBrandName() {
  try {
    if (typeof window !== 'undefined' && window.location.hostname.includes('cheepsweepz')) {
      return 'CheepSweepz';
    }
  } catch (_) {
    /* ignore hostname lookup failures */
  }
  return 'CheepSweepz';
}

export default function SubmitProductPage() {
  const navigate = useNavigate();
  const brandName = safeBrandName();

  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setError(null);

      const trimmedUrl = String(url || '').trim();
      if (!trimmedUrl) {
        const message = 'Paste a product link to continue.';
        setError(message);
        toast.error(message);
        return;
      }

      setLoading(true);

      const response = await fetch('/api/listings/extract-web-asset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productUrl: trimmedUrl }),
      });

      let data = {};
      try {
        data = await response.json();
      } catch (_) {
        data = {};
      }

      if (!response.ok) {
        const message = data.error || data.detail || `Extraction failed (${response.status})`;
        setError(message);
        toast.error(message);
        return;
      }

      if (data.success && data.newListingId) {
        toast.success('Product extracted — sending to checkout...');
        navigate(`/cart?action=add&id=${encodeURIComponent(data.newListingId)}`);
        return;
      }

      const message = data.error || 'Could not create a verified listing for checkout.';
      setError(message);
      toast.message('Extraction incomplete', { description: message });
    } catch (submitError) {
      const message = submitError?.message || 'Submission failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-slate-50 text-slate-900">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {brandName}
        </Link>

        <div className="mb-8">
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded-2xl bg-orange-500/15 p-3 text-orange-600">
              <Package className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">
                Submit Link
              </p>
              <h1
                className="text-3xl font-bold text-slate-900 sm:text-4xl"
                data-testid="submit-title"
              >
                Paste a product link — we&apos;ll import it with customs included
              </h1>
            </div>
          </div>
          <p className="text-lg text-slate-600">
            Shop on Amazon, Walmart, eBay, Temu, AliExpress, or Alibaba. Copy the product link,
            paste it below, and we&apos;ll extract pricing via Bright Data Web Unlocker, apply the
            20% corporate margin on the server, and drop it into checkout.
          </p>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {STEPS.map((step, index) => (
            <div
              key={step.title}
              className="flex items-start gap-3 rounded-2xl bg-orange-50 p-4 ring-1 ring-orange-100"
            >
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-orange-500 text-sm font-bold text-white">
                {index + 1}
              </div>
              <div>
                <p className="font-semibold text-slate-900">{step.title}</p>
                <p className="text-sm text-slate-600">{step.detail}</p>
              </div>
            </div>
          ))}
        </div>

        <section className="mb-8">
          <div className="mb-4 flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-orange-600" />
            <h2 className="text-lg font-bold text-slate-900">Browse marketplaces</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {MARKETPLACES.map((marketplace) => (
              <a
                key={marketplace.id}
                href={marketplace.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left shadow-sm transition hover:border-orange-300 hover:shadow-md"
              >
                <span className="font-semibold text-slate-900">{marketplace.label}</span>
                <ExternalLink className="h-4 w-4 text-slate-400 transition group-hover:text-orange-500" />
              </a>
            ))}
          </div>
        </section>

        <div className="rounded-3xl border-2 border-orange-200 bg-white p-6 shadow-lg shadow-orange-500/10 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block text-sm font-medium text-slate-800" htmlFor="product-url">
              Product URL
            </label>
            <div className="relative">
              <Link2 className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="product-url"
                type="text"
                inputMode="url"
                autoComplete="off"
                placeholder="https://www.temu.com/channel/lightning-deals/..."
                value={url}
                onChange={(event) => {
                  setUrl(event.target.value);
                  if (error) setError(null);
                }}
                className="h-14 rounded-2xl border-slate-200 bg-white pl-11 text-base text-slate-900 placeholder:text-slate-400"
              />
            </div>

            {error ? (
              <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              disabled={loading || !url.trim()}
              className="h-14 w-full rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 text-base font-semibold text-white hover:from-orange-600 hover:to-orange-700 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Extracting and adding to cart...
                </>
              ) : (
                'Submit Product Link'
              )}
            </Button>
          </form>
        </div>

        <p className="mt-8 text-center text-xs text-slate-500">
          Product images and descriptions are provided for reference only. CheepSweepz is not
          affiliated with or endorsed by Amazon, eBay, AliExpress, Temu, Walmart, or Alibaba.
        </p>
      </div>
    </div>
  );
}
